/**
 * The viewer's background preferences: which effects are on, and where the
 * background gradient comes from and in what colour.
 *
 * All of them are scalars inside `users/{uid}.settings`, for the reason the
 * storefront keys are (see UserSettings in src/context/auth-context.tsx): a map
 * there typechecks, passes rules, and is then deleted by the onUserUpdated
 * repair.
 *
 * EACH MODE HAS ITS OWN BACKGROUND (owner, 2026-10-23). Dark mode has the
 * starfield (`showStarfield`), light mode the dot grid (`showDotGrid`), and
 * both are on by default; neither ever shows in the other mode. The planets
 * are kept per theme (owner decision 2026-09-30): `showPlanets` is the
 * dark-mode switch and `showPlanetsInLight` the light-mode one, so a viewer who
 * turns planets on in light mode, switches to dark and back, finds each mode
 * as they left it. The gradient is dark-mode only (owner decision
 * 2026-09-30), so it has one switch. Light mode used to have a starfield of
 * its own, `showStarfieldLight`; the grid replaced it, and a stored value is
 * no longer read.
 *
 * THE GRADIENT IS OFF BY DEFAULT (owner, 2026-10-23), AND THE DARK-MODE
 * PLANETS ARE ON (owner, 2026-10-26; off from 2026-10-23). Light-mode planets
 * are off, and were turned off for everyone on 2026-10-26 (owner): the switch
 * moved from `showPlanetsLight` to `showPlanetsInLight`, so a light-mode
 * planets-on stored before then is no longer read, and a viewer who wants
 * them back turns the switch on again. Older accounts were stamped
 * `showStarfield: true` and `showGradient: true` at signup
 * (createVerifiedUserProfile in functions/src/index.ts), which reads as a
 * choice, so they keep the gradient until the viewer switches it off. A false
 * that Reset appearance wrote while a switch was off by default (the starfield
 * on 2026-10-22 or 2026-10-23, the planets from 2026-10-23 to 2026-10-26)
 * reads as a choice too.
 *
 * Phones get fewer planets and fewer stars, by viewport area
 * (src/lib/cosmos-model.ts). Accounts reset while phones defaulted planets
 * off (2026-09-30 to 2026-10-04) hold `showPlanets: null`, which reads as the
 * default.
 *
 * Pure on purpose. ThemeProvider is a `.tsx` the type-stripping test runner
 * cannot load, and "what colour is the gradient on this page" has enough
 * precedence in it (seller override, viewer pick, default) to be worth a test.
 * src/lib/background-prefs.test.ts covers every branch here.
 */

export type GradientDirection = 'top' | 'bottom';

/**
 * Each switch's default when the key is missing: the dark-mode starfield and
 * planets and the light-mode dot grid on, the gradient and light-mode planets
 * off (owner, 2026-10-23; dark-mode planets on since 2026-10-26). A missing
 * key has to read as "never chose", since many accounts predate one key or
 * another.
 */
export const BACKGROUND_DEFAULTS = {
  showStarfield: true,
  showGradient: false,
  showPlanets: true,
  showPlanetsInLight: false,
  showDotGrid: true,
} as const;

/**
 * The patch "Reset appearance" writes, in ONE updateUser call. Every switch
 * goes back to its default and both gradient keys go back to '' (automatic),
 * not to whatever the automatic value is right now, so the reset keeps
 * following the accent.
 */
export const BACKGROUND_RESET_PATCH = {
  ...BACKGROUND_DEFAULTS,
  gradientDirection: '',
  gradientColor: '',
} as const;

/** Every background switch, as the viewer has it. */
export type BackgroundPrefs = { -readonly [K in keyof typeof BACKGROUND_DEFAULTS]: boolean };

/**
 * The viewer's background switches, each defaulted. `settings` is the raw
 * stored map (or the guest defaults), untrusted.
 */
export function backgroundPrefs(settings: Record<string, unknown>): BackgroundPrefs {
  return {
    showStarfield: boolPref(settings.showStarfield, BACKGROUND_DEFAULTS.showStarfield),
    showGradient: boolPref(settings.showGradient, BACKGROUND_DEFAULTS.showGradient),
    showPlanets: boolPref(settings.showPlanets, BACKGROUND_DEFAULTS.showPlanets),
    showPlanetsInLight: boolPref(settings.showPlanetsInLight, BACKGROUND_DEFAULTS.showPlanetsInLight),
    showDotGrid: boolPref(settings.showDotGrid, BACKGROUND_DEFAULTS.showDotGrid),
  };
}

/**
 * A stored boolean, or `fallback` when the key is missing or holds anything
 * else. Every existing account predates `showPlanets`, and some predate the
 * other two, so a raw read would turn "never chose" into "chose off".
 */
export function boolPref(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

/** 'top' or 'bottom', or null for '' (automatic), a missing key, or junk. */
export function normaliseGradientDirection(value: unknown): GradientDirection | null {
  return value === 'top' || value === 'bottom' ? value : null;
}

/**
 * A `#rrggbb` colour, lowercased, or null for '' (automatic), a missing key, or
 * anything that is not a six-digit hex. The value ends up in a CSS custom
 * property on <html>, and a stored document is not a trusted source.
 */
export function normaliseGradientColor(value: unknown): string | null {
  if (typeof value !== 'string' || !/^#[0-9a-fA-F]{6}$/.test(value)) return null;
  return value.toLowerCase();
}

/**
 * Where the gradient comes from when nobody chose: from below, a glow rising
 * off the page end, the look the site has always had. The gradient only
 * renders in dark mode, so there is no per-theme default any more.
 */
export const DEFAULT_GRADIENT_DIRECTION: GradientDirection = 'bottom';

/**
 * The colour the gradient falls back to if even the accent is malformed: the
 * platform default accent (DEFAULT_ACCENT_COLOR in src/context/theme-context.tsx,
 * which cannot be imported here: it is a `.tsx`).
 */
export const FALLBACK_GRADIENT_COLOR = '#625ff7';

/**
 * The direction actually painted: a seller's explicit pick on their own page,
 * then the viewer's explicit pick, then from below.
 */
export function resolveGradientDirection(overrideDirection: unknown, stored: unknown): GradientDirection {
  return normaliseGradientDirection(overrideDirection)
    ?? normaliseGradientDirection(stored)
    ?? DEFAULT_GRADIENT_DIRECTION;
}

/**
 * The colour actually painted: a seller's explicit pick on their own page, then
 * the viewer's explicit pick, then `activeAccent`.
 *
 * The caller must pass `activeAccent` as the accent PAINTED on this page (a
 * seller's accent while their page is open, otherwise the brand violet;
 * visitors have had no accent of their own since 2026-10-21). A malformed
 * accent falls through to the platform default rather than reaching the CSS.
 */
export function resolveGradientColor(overrideColor: unknown, stored: unknown, activeAccent: unknown): string {
  return normaliseGradientColor(overrideColor)
    ?? normaliseGradientColor(stored)
    ?? normaliseGradientColor(activeAccent)
    ?? FALLBACK_GRADIENT_COLOR;
}

/**
 * What a direction setter writes: the direction, or '' for automatic. '' and
 * not deleteField(): updateUser merges the patch into React state before the
 * write, so a FieldValue sentinel would land in `user.settings`.
 */
export function gradientDirectionSetting(direction: GradientDirection | null): GradientDirection | '' {
  return normaliseGradientDirection(direction) ?? '';
}

/**
 * What a colour setter writes: the lowercased hex, '' for null (automatic), or
 * undefined for an invalid colour, which the caller must treat as "write
 * nothing" rather than as a reset.
 */
export function gradientColorSetting(hex: string | null): string | undefined {
  if (hex === null) return '';
  return normaliseGradientColor(hex) ?? undefined;
}
